import { d as debounce } from './debounce-CFN_LA0O.js';
import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';
import { f as fetchHTML } from './fetchHTML-CGNR3V1z.js';
import './parseHTML-Om2c1Ayj.js';

class PredictiveSearchResults extends HTMLElement {
  #unsubscribers = [];
  #drawerId = this.closest('drawer-element')?.id;
  #abortController;
  #options = {
    minimumSearchTermLength: 2,
    searchInputId: this.dataset.searchInputId,
    hideElement: this.dataset.hideElement,
    resourcesLimit: 10,
    resourcesLimitScope: 'each',
    resourcesFields:
      'author,body,product_type,tag,title,variants.barcode,variants.sku,variants.title,vendor',
  };
  #elementsToHide;

  connectedCallback() {
    if (this.#options.hideElement) {
      this.#elementsToHide = document.querySelector(`#${this.#options.hideElement}`);
    }

    // Handle search clear button
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-search-clear]')) {
        this.#requestClearSearch();
      }
    });

    // Handle search input
    this.#unsubscribers.push(
      PubSub.subscribe('search:input', ({ id, data: { value } }) => {
        if (id !== this.dataset.searchInputId) return;
        this.#enableLoaders();
        this.#updateSearch(value);
      }),
      PubSub.subscribe('search:clear', ({ id }) => {
        id === this.dataset.searchInputId && this.#onClearSearchInput();
      }),
      this.#drawerId &&
        PubSub.subscribe('drawer:open', ({ id }) => {
          if (id !== this.#drawerId) return;
          this.#requestClearSearch();
          this.#clearSearchResults();
        })
    );
  }

  disconnectedCallback() {
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
  }

  /**
   * Clear the search results
   */
  #clearSearchResults() {
    this.innerHTML = '';
  }

  /**
   * Request to clear the search input via pubsub
   */
  #requestClearSearch() {
    if (!this.#options.searchInputId) return;
    PubSub.publish('search:request-clear', { id: this.#options.searchInputId });
  }

  /**
   * Handle when search input is cleared
   */
  #onClearSearchInput() {
    this.ariaHidden = true;
    this.#hideOnSearchResults(false);
  }

  /**
   * Enable loaders
   */
  #enableLoaders() {
    this.querySelectorAll('[data-loader]').forEach((loader) => {
      loader.ariaHidden = false;
    });
  }

  /**
   * Disable loaders
   */
  #disableLoaders() {
    setTimeout(() => {
      this.querySelectorAll('[data-loader]').forEach((loader) => {
        loader.ariaHidden = true;
      });
    }, 50);
  }

  /**
   * Update the search
   */
  #updateSearch = debounce((searchTerm) => this.#getSearchResults(searchTerm), 300);

  /**
   * Get the search results
   */
  #getSearchResults(searchTerm) {
    searchTerm = searchTerm.trim();
    if (searchTerm.length < this.#options.minimumSearchTermLength) {
      this.#clearSearchResults();
      this.#hideOnSearchResults(false);
      this.ariaHidden = true;
      return;
    }
    this.#fetchSearchResults(searchTerm);
  }

  /**
   * Hide other elements when the predictive search results are shown
   */
  #hideOnSearchResults(hide = true) {
    if (this.#elementsToHide) this.#elementsToHide.ariaHidden = hide;
  }

  /**
   * Fetch the search results
   */
  async #fetchSearchResults(searchTerm) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const searchUrl = routes.predictive_search_url;
      const html = await fetchHTML(
        `${searchUrl}?q=${searchTerm}&section_id=fetch-predictive-search&resources[limit]=${this.#options.resourcesLimit}&resources[limit_scope]=${this.#options.resourcesLimitScope}&resources[options][fields]=${this.#options.resourcesFields}`,
        { signal: this.#abortController.signal }
      );
      const resultsMarkup = html.querySelector('predictive-search-results').innerHTML;
      this.innerHTML = '';
      this.innerHTML = resultsMarkup;
      this.ariaHidden = false;
      this.#hideOnSearchResults();
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      this.#disableLoaders();
    }
  }
}

customElements.define('predictive-search-results', PredictiveSearchResults);
